/* The floor: real org Space + app.atrium.floor.layout + live occupancy/heat/
   presence. No mock world — if no floor Space is advertised or seeded, `active`
   stays false and the UI shows an honest empty state. */
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'
import { MatrixError } from '@/types/matrix'
import { usePresence } from '@/stores/presence'
import { useToasts } from '@/stores/toasts'
import {
  botMxid, colorFor, localpart, rtcMemberMxid, shortName,
} from '@/lib/util'
import {
  isPresenceMode, roomTypeMeta, type Hood, type Person, type Room, type RoomKind, type TeamInfo,
} from '@/types/atrium'
import type { StateEvent } from '@/types/matrix'

interface LayoutRoom {
  id: string
  room_id: string
  name: string
  kind?: RoomKind
  hood?: string
  x: number; y: number; w: number; h: number
  accent?: string
  activity?: string
  team?: string
  external?: boolean
  incident?: boolean
}
interface LayoutContent { rooms: LayoutRoom[]; hoods?: Hood[]; version?: number }

interface FloorState {
  active: boolean
  loaded: boolean
  orgSpace: string | null
  rooms: Room[]
  people: Record<string, Person>
  /** MXIDs the Presence Engine reports as having Atrium open right now. */
  online: string[]
  hoods: Hood[]
  teams: Record<string, TeamInfo>
  myTeams: string[]
  /** roomId → slug of rooms we knocked on, waiting to be let in. */
  pendingKnocks: Record<string, string>
  slugToRoom: Record<string, string>
  roomToSlug: Record<string, string>
  activeSlug: string | null
  refreshing: boolean
}

export const useFloor = defineStore('floor', {
  state: (): FloorState => ({
    active: false,
    loaded: false,
    orgSpace: null,
    rooms: [],
    people: {},
    online: [],
    hoods: [],
    teams: {},
    myTeams: [],
    pendingKnocks: {},
    slugToRoom: {},
    roomToSlug: {},
    activeSlug: null,
    refreshing: false,
  }),

  getters: {
    peopleArr: (s): Person[] => Object.values(s.people),
    /** People who have Atrium open right now (engine liveness) but aren't in
     *  any room/call — the floor's "lounge". You appear here too (you're
     *  obviously online) whenever you're not in a room and not set to away. */
    available: (s): Person[] => {
      const inRoom = new Set(s.rooms.flatMap((r) => r.occupants))
      const online = new Set(s.online)
      const myId = matrix.userId()
      return Object.values(s.people)
        .filter((p) => !p.guest && p.mode !== 'away' && !inRoom.has(p.id) && (online.has(p.id) || p.id === myId))
        .sort((a, b) => (a.you === b.you ? a.short.localeCompare(b.short) : a.you ? -1 : 1))
    },
    you: (s): Person | null => {
      const id = matrix.userId()
      return id ? s.people[id] ?? null : null
    },
    roomBySlug: (s) => (slug: string): Room | undefined => s.rooms.find((r) => r.id === slug),
    occupantsOf: (s) => (slug: string): Person[] => {
      const room = s.rooms.find((r) => r.id === slug)
      if (!room) return []
      return [...new Set(room.occupants)].map((id) => s.people[id]).filter(Boolean) as Person[]
    },
    roomId: (s) => (slug: string): string | undefined => s.slugToRoom[slug],
    slugOf: (s) => (roomId: string): string | null => s.roomToSlug[roomId] ?? null,
    teamsArr: (s): TeamInfo[] => Object.values(s.teams).sort((a, b) => a.name.localeCompare(b.name)),
    /** Can I walk straight into this room? (No team, or it's my team.) */
    canWalkIn: (s) => (room: Room): boolean => !room.team || s.myTeams.includes(room.team),
  },

  actions: {
    async loadFloor(): Promise<boolean> {
      // Discovery can have been cached before the operator created the floor.
      // Recheck the server before showing the setup screen for a missing Space.
      const org = matrix.floorSpace() || (await matrix.refreshDiscovery()).floorSpace
      if (!org) { this.active = false; this.loaded = true; return false }
      this.orgSpace = org
      try { await matrix.joinRoom(org) } catch { /* already joined */ }

      const layout = await matrix.stateEvent<LayoutContent>(org, 'app.atrium.floor.layout', '')
      if (!layout || !layout.rooms) { this.active = false; this.loaded = true; return false }

      this.hoods = layout.hoods || []
      const slugToRoom: Record<string, string> = {}
      const roomToSlug: Record<string, string> = {}
      const rooms: Room[] = layout.rooms.map((e) => {
        slugToRoom[e.id] = e.room_id
        roomToSlug[e.room_id] = e.id
        return {
          id: e.id, roomId: e.room_id, name: e.name,
          kind: (e.kind || 'project') as RoomKind,
          hood: e.hood || '', x: e.x, y: e.y, w: e.w, h: e.h,
          accent: e.accent || 'var(--p-open)',
          team: e.team || undefined,
          external: !!e.external, incident: !!e.incident,
          occupants: [], heat: 0, sharers: [], cams: 0, boardOpen: false, tasksOpen: false,
          activity: e.activity || '',
        }
      })
      this.slugToRoom = slugToRoom
      this.roomToSlug = roomToSlug

      const bot = botMxid(matrix.userId())
      const people: Record<string, Person> = {}
      const ensurePerson = (mxid: string, name?: string | null, avatar?: string | null): Person => {
        if (!people[mxid]) {
          people[mxid] = {
            id: mxid, name: name || localpart(mxid), short: shortName(name, mxid),
            color: colorFor(mxid), mode: 'open', now: '', you: mxid === matrix.userId(),
          }
        } else if (name && people[mxid].name === localpart(mxid)) {
          people[mxid].name = name
          people[mxid].short = shortName(name, mxid)
        }
        if (avatar) people[mxid].avatarUrl = avatar
        return people[mxid]
      }

      const members = await matrix.joinedMembers(org)
      Object.entries(members.joined || {}).forEach(([mxid, info]) => {
        if (mxid === bot) return
        ensurePerson(mxid, info.display_name, info.avatar_url)
      })

      // Presence projection lives as state in the org Space.
      const orgState = await matrix.roomState(org)

      // Teams: the registry is org-Space state; my memberships are which team
      // Spaces I'm joined to (assignment joins me server-side).
      const teams: Record<string, TeamInfo> = {}
      orgState
        .filter((ev) => ev.type === 'app.atrium.team' && (ev.content as any)?.space_id)
        .forEach((ev) => {
          const c = ev.content as { name?: string; space_id: string }
          teams[ev.state_key] = { id: ev.state_key, name: c.name || ev.state_key, spaceId: c.space_id }
        })
      this.teams = teams
      try {
        const joined = new Set(await matrix.joinedRooms())
        this.myTeams = Object.values(teams).filter((t) => joined.has(t.spaceId)).map((t) => t.id)
      } catch { /* keep previous memberships on a transient error */ }
      // Who has Atrium open right now (engine-tracked liveness singleton).
      const onlineEv = orgState.find((ev) => ev.type === 'app.atrium.floor.online')
      const onlineList = (onlineEv?.content as any)?.users
      this.online = Array.isArray(onlineList) ? (onlineList.filter((u) => typeof u === 'string') as string[]) : []

      orgState
        .filter((ev) => ev.type === 'app.atrium.presence.member' && ev.content && (ev.content as any).mode)
        .forEach((ev) => {
          const c = ev.content as any
          const mxid = c.user || '@' + ev.state_key
          if (mxid === bot) return
          const p = ensurePerson(mxid, null)
          p.mode = isPresenceMode(c.mode) ? c.mode : 'open'
          p.now = c.now || ''
          if (c.accessory) p.accessory = c.accessory
        })

      // Calendar projection (app.atrium.calendar.member): who linked Google and
      // whether their calendar says they're in a meeting right now (§10.4).
      orgState
        .filter((ev) => ev.type === 'app.atrium.calendar.member' && ev.content && (ev.content as any).connected)
        .forEach((ev) => {
          const c = ev.content as any
          const mxid = c.user || '@' + ev.state_key
          if (mxid === bot) return
          const p = ensurePerson(mxid, null)
          p.calendarConnected = true
          if (typeof c.email === 'string' && c.email) p.email = c.email
          if (c.in_meeting && c.meeting && (!c.meeting.end || c.meeting.end > Date.now())) {
            p.meeting = {
              summary: c.meeting.summary || '',
              start: c.meeting.start,
              end: c.meeting.end,
              meet: !!c.meeting.meet,
              slug: c.meeting.slug || undefined,
            }
          }
        })

      // Always include me.
      const myId = matrix.userId()
      if (myId) {
        const me = ensurePerson(myId, null)
        me.you = true
        const presence = usePresence()
        me.mode = presence.myMode
        me.now = presence.myNow
        const prof = await matrix.profile(myId)
        if (prof.displayname) { me.name = prof.displayname; me.short = shortName(prof.displayname, myId) }
        // Profile beats the (possibly stale) member event — including removal.
        me.avatarUrl = prof.avatar_url || undefined
      }

      // Team rooms run with `joined` history visibility so knock-admitted guests
      // can't read past chat — which also makes them un-peekable. The Presence
      // Engine mirrors their live floor signals into the org Space keyed by room
      // id (app.atrium.floor.room); we read those instead of peeking the room.
      const mirrors: Record<string, any> = {}
      orgState
        .filter((ev) => ev.type === 'app.atrium.floor.room' && ev.content && Object.keys(ev.content).length > 0)
        .forEach((ev) => { mirrors[ev.state_key] = ev.content as any })

      // Occupancy + heat + media + board per room (RTC membership = occupancy).
      await Promise.all(rooms.map(async (r) => {
        if (r.team) {
          // Team room: paint from the engine's projection, not a (forbidden) peek.
          const m = mirrors[r.roomId]
          if (!m) return // not projected yet — honest empty
          r.occupants = (Array.isArray(m.occupants) ? m.occupants : []).filter((x: string) => x !== bot)
          r.occupants.forEach((mxid) => ensurePerson(mxid, members.joined[mxid]?.display_name, members.joined[mxid]?.avatar_url))
          r.sharers = (Array.isArray(m.sharers) ? m.sharers : []).filter((x: string) => x !== bot)
          r.cams = typeof m.cams === 'number' ? m.cams : 0
          r.heat = typeof m.heat_pct === 'number' ? m.heat_pct / 100 : Math.min(r.occupants.length / 8, 1)
          if (m.board) { r.boardOpen = !!m.board.open; r.boardBy = m.board.by || '' }
          if (m.tasks) { r.tasksOpen = !!m.tasks.open; r.tasksBy = m.tasks.by || '' }
          if (m.timer?.running && m.timer.ends_at > Date.now()) r.pomo = { phase: m.timer.phase === 'break' ? 'break' : 'focus', endsAt: m.timer.ends_at }
          if (m.incident?.active) { r.incident = true; r.incidentInfo = { title: m.incident.title || '', since: m.incident.since } }
          if (m.officehours) r.doorsOpen = !!m.officehours.open
          r.queueLen = typeof m.queue_len === 'number' ? m.queue_len : 0
          return
        }
        try {
          const st = await matrix.roomState(r.roomId)
          const rtc = st.filter((ev) =>
            (ev.type === 'm.rtc.member' || ev.type === 'org.matrix.msc3401.call.member') &&
            ev.content && Object.keys(ev.content).length > 0)
          const mxidOf = (ev: StateEvent): string | null => (ev.content as any).user || rtcMemberMxid(ev.state_key)
          r.occupants = [...new Set(rtc.map(mxidOf).filter(Boolean) as string[])].filter((m) => m !== bot)
          r.occupants.forEach((mxid) => ensurePerson(mxid, members.joined[mxid]?.display_name, members.joined[mxid]?.avatar_url))
          const flagged = (flag: string): string[] =>
            [...new Set(rtc.filter((ev) => (ev.content as any)[flag]).map(mxidOf).filter(Boolean) as string[])]
              .filter((m) => m !== bot)
          r.sharers = flagged('screen')
          r.cams = flagged('camera').length
          const heat = st.find((ev) => ev.type === 'app.atrium.room.heat')
          r.heat = heat?.content ? ((heat.content as any).heat_pct || 0) / 100 : Math.min(r.occupants.length / 8, 1)
          const bd = st.find((ev) => ev.type === 'app.atrium.room.board')
          if (bd?.content) {
            r.boardOpen = !!(bd.content as any).open
            r.boardBy = (bd.content as any).by || ''
          }
          const td = st.find((ev) => ev.type === 'app.atrium.room.tasks')
          if (td?.content) {
            r.tasksOpen = !!(td.content as any).open
            r.tasksBy = (td.content as any).by || ''
          }
          // Kind-specific live signals — what the floor can read off the door.
          const timer = st.find((ev) => ev.type === 'app.atrium.timer')?.content as any
          if (timer?.running && timer.ends_at > Date.now()) r.pomo = { phase: timer.phase === 'break' ? 'break' : 'focus', endsAt: timer.ends_at }
          const inc = st.find((ev) => ev.type === 'app.atrium.incident')?.content as any
          if (inc?.active) {
            r.incident = true
            r.incidentInfo = { title: inc.title || '', since: inc.since }
          }
          const oh = st.find((ev) => ev.type === 'app.atrium.officehours')?.content as any
          if (oh) r.doorsOpen = !!oh.open
          r.queueLen = st.filter((ev) => ev.type === 'app.atrium.queue' && (ev.content as any)?.ts).length
        } catch { /* leave empty */ }
      }))

      this.rooms = rooms
      this.people = people
      this.active = true
      this.loaded = true
      return true
    },

    async refresh(): Promise<void> {
      if (!this.active || this.refreshing) return
      this.refreshing = true
      try { await this.loadFloor() } finally { this.refreshing = false }
    },

    async enterRoom(slug: string): Promise<void> {
      const roomId = this.slugToRoom[slug]
      const myId = matrix.userId()
      if (!roomId || !myId) return
      try {
        await matrix.joinRoom(roomId)
      } catch (e) {
        // Team rooms are knock_restricted: a 403 means the protocol wants us to
        // knock — the people inside get a "let them in" prompt.
        const room = this.rooms.find((r) => r.id === slug)
        if (room?.team && e instanceof MatrixError && e.httpStatus === 403) {
          await this.knockOnRoom(roomId, room)
          return
        }
        /* otherwise: transient / already joined — proceed as before */
      }
      // Room presence IS call membership: the m.rtc.member signal is published
      // by the call layer once the call actually connects (call.ts emitState →
      // publishMedia), not by navigation. Entering a room means joining its call.
      setTimeout(() => void this.refresh(), 1200)
    },

    async knockOnRoom(roomId: string, room: Room): Promise<void> {
      if (this.pendingKnocks[roomId]) return // already waiting at the door
      const teamName = (room.team && this.teams[room.team]?.name) || 'the team'
      try {
        await matrix.knockRoom(roomId, `wants to join ${room.name}`)
        this.pendingKnocks[roomId] = room.id
        useToasts().push({
          icon: 'knock', color: room.accent,
          title: `Knocked on ${room.name}`,
          text: `It's a ${teamName} room — someone inside can let you in.`, ms: 7000,
        })
      } catch (e) {
        // Knocking twice (still pending server-side) is also a 403 — treat as waiting.
        if (e instanceof MatrixError && e.httpStatus === 403) {
          this.pendingKnocks[roomId] = room.id
          return
        }
        useToasts().push({ icon: 'x', color: 'var(--danger)', title: 'Couldn’t knock', text: e instanceof Error ? e.message : undefined })
      }
    },

    /** A sync invite arrived. If it answers one of our knocks, walk in. */
    async onInvite(roomId: string): Promise<void> {
      const slug = this.pendingKnocks[roomId]
      if (!slug) return
      delete this.pendingKnocks[roomId]
      const room = this.rooms.find((r) => r.id === slug)
      try {
        await matrix.joinRoom(roomId)
      } catch (e) {
        console.warn('[floor] join after knock-accept failed', e)
        return
      }
      useToasts().push({
        icon: 'door', color: room?.accent || 'var(--p-open)',
        title: 'The door opened', text: `You're in ${room?.name || 'the room'} now.`,
        action: this.activeSlug === slug ? undefined : 'Enter',
        onAction: () => window.location.assign('#/room/' + slug),
        ms: 8000,
      })
      // Already looking at the room? Complete the entry (RTC membership).
      if (this.activeSlug === slug) await this.enterRoom(slug)
      void this.refresh()
    },

    async leaveRoom(slug: string): Promise<void> {
      const roomId = this.slugToRoom[slug]
      const myId = matrix.userId()
      if (!roomId || !myId) return
      // m.rtc.member is keyed by user, not device: when another of my tabs or
      // devices is still in this room's call, the occupancy signal is theirs
      // now — clearing it here would make the floor think I left entirely.
      if (usePresence().otherDeviceInCallRoom === roomId) return
      try { await matrix.sendState(roomId, 'm.rtc.member', myId, {}) } catch { /* best effort */ }
      // Guests of team rooms are members only while inside: leaving the room
      // also leaves the Matrix room, so the next visit knocks again. (The
      // presence-engine sweeps this server-side too, in case we never get here.)
      // Walking away while still knocking retracts the knock the same way.
      const room = this.rooms.find((r) => r.id === slug)
      if (room?.team && !this.canWalkIn(room)) {
        delete this.pendingKnocks[roomId]
        await matrix.leaveRoom(roomId)
      }
    },

    /** Ambient persistence: viewing the floor/other pages does NOT leave your
     *  room; only entering a *different* room switches you. */
    async syncActiveRoom(routeName: string | undefined, slug: string | null): Promise<void> {
      if (!this.active) return
      const target = routeName === 'room' ? slug : null
      if (!target) return // looking elsewhere keeps your current room
      if (target === this.activeSlug) return
      const prev = this.activeSlug
      this.activeSlug = target
      if (prev) await this.leaveRoom(prev)
      await this.enterRoom(target)
    },

    async hangUp(): Promise<void> {
      const slug = this.activeSlug
      this.activeSlug = null
      if (slug) await this.leaveRoom(slug)
    },

    /** Re-publish our m.rtc.member with current media flags so others see who's
     *  sharing/on-camera. Keeps membership (occupancy) even when media is off. */
    async publishMedia(slug: string, media: { mic: boolean; cam: boolean; screen: boolean }): Promise<void> {
      const roomId = this.slugToRoom[slug]
      const myId = matrix.userId()
      if (!roomId || !myId) return
      try {
        await matrix.sendState(roomId, 'm.rtc.member', myId, {
          user: myId, call_id: '', application: 'm.call',
          device_id: matrix.getSession()?.deviceId || 'web', intent: 'ambient',
          mic: media.mic, camera: media.cam, screen: media.screen, ts: Date.now(),
        })
      } catch (e) { console.warn('[floor] publishMedia failed', e) }
    },

    // `silent` is for the auto-close path (the last viewer stepped back to the
    // call): we shouldn't toast a permission error there — it's a background
    // tidy-up, not something the user asked for.
    async setBoard(slug: string, open: boolean, by: string, silent = false): Promise<void> {
      const roomId = this.slugToRoom[slug]
      if (!roomId) return
      // Optimistic: open/close for me now; publish so it opens for everyone.
      const r = this.rooms.find((x) => x.id === slug)
      const prev = r ? r.boardOpen : false
      if (r) { r.boardOpen = open; r.boardBy = by }
      try {
        await matrix.sendState(roomId, 'app.atrium.room.board', '', { open, by, ts: Date.now() })
      } catch (e) {
        if (r) r.boardOpen = prev // revert — don't leave it falsely open
        if (!silent) {
          const denied = e instanceof MatrixError && e.httpStatus === 403
          useToasts().push({
            icon: 'board', color: 'var(--danger)',
            title: denied ? 'Can’t open the whiteboard here' : 'Couldn’t change the whiteboard',
            text: denied ? 'You don’t have permission in this room.' : undefined,
          })
        }
        console.warn('[floor] setBoard failed', e)
      }
    },

    async setTasks(slug: string, open: boolean, by: string, silent = false): Promise<void> {
      const roomId = this.slugToRoom[slug]
      if (!roomId) return
      // Optimistic: open/close for me now; publish so it opens for everyone.
      const r = this.rooms.find((x) => x.id === slug)
      const prev = r ? r.tasksOpen : false
      if (r) { r.tasksOpen = open; r.tasksBy = by }
      try {
        await matrix.sendState(roomId, 'app.atrium.room.tasks', '', { open, by, ts: Date.now() })
      } catch (e) {
        if (r) r.tasksOpen = prev // revert — don't leave it falsely open
        if (!silent) {
          const denied = e instanceof MatrixError && e.httpStatus === 403
          useToasts().push({
            icon: 'kanban', color: 'var(--danger)',
            title: denied ? 'Can’t open the task board here' : 'Couldn’t change the task board',
            text: denied ? 'You don’t have permission in this room.' : undefined,
          })
        }
        console.warn('[floor] setTasks failed', e)
      }
    },
  },
})

/** Derive the honest one-line "what's happening" from real signals only. */
export function roomActivity(r: Room): { activity: string; sharing: boolean; board: boolean; cams: number } {
  if (r.incident && r.incidentInfo) {
    return { activity: `incident: ${r.incidentInfo.title || 'active'}`, sharing: false, board: r.boardOpen, cams: r.cams }
  }
  if (r.sharers.length) {
    const extra = r.sharers.length > 1 ? ` +${r.sharers.length - 1}` : ''
    return { activity: `sharing a screen${extra}`, sharing: true, board: r.boardOpen, cams: r.cams }
  }
  if (r.boardOpen) return { activity: 'Whiteboard open', sharing: false, board: true, cams: r.cams }
  if (r.tasksOpen) return { activity: 'Task board open', sharing: false, board: false, cams: r.cams }
  if (r.pomo && r.pomo.endsAt > Date.now()) {
    const left = Math.ceil((r.pomo.endsAt - Date.now()) / 60_000)
    return { activity: `${r.pomo.phase === 'break' ? 'break' : 'focus session'} · ${left}m left`, sharing: false, board: false, cams: r.cams }
  }
  if (r.kind === 'office-hours' && r.doorsOpen !== undefined) {
    const waiting = r.queueLen ? ` · ${r.queueLen} waiting` : ''
    return { activity: r.doorsOpen ? `doors open${waiting}` : 'doors closed', sharing: false, board: false, cams: r.cams }
  }
  if (r.cams > 0) return { activity: `${r.cams} on camera`, sharing: false, board: false, cams: r.cams }
  return { activity: r.activity || roomTypeMeta(r.kind).label, sharing: false, board: false, cams: 0 }
}
